// Komponen cetak faktur (A4 / A5 / continuous_half) — dipakai:
//  - FakturSettings.jsx: pratinjau hidup saat owner mendesain template
//  - FakturPublik.jsx: dokumen yang dibuka pelanggan dari tautan WhatsApp
//  - (menyusul) Sales.jsx/InputNota.jsx: tombol cetak dipasang agen integrasi
//
// KONTRAK PEMAKAIAN — buat komponen ini "mudah dipakai" oleh siapa pun:
//   <FakturPrint data={sale|po|transfer} template={template} />
// `data` boleh berupa entitas mentah (sale/po/transfer) ATAU seluruh payload
// GET /api/faktur/data/... atau /api/faktur/publik/{token} (yang membungkus
// entitas di dalam key sale/po/transfer + template + csat_link + no_faktur) —
// keduanya dikenali otomatis lewat `normalisasiEntitas`, supaya pemanggil
// tidak perlu membongkar responsnya dulu.
import { Fragment, useEffect, useState } from "react";
import { Printer } from "lucide-react";
import WarrantyTerms from "./WarrantyTerms";
import { rincianPenjualanCetak, labelTempo } from "../../lib/notaPenjualan";

// Dimensi kertas dalam mm (dipakai untuk @page CSS saat mencetak DAN untuk
// lebar maksimum pratinjau di layar). Nilainya ditulis apa adanya seperti
// kertas itu dikenal orang — continuous_half memang biasa disebut "210x140"
// (mendatar). `resolveDim` yang MENORMALKAN pasangan ini ke potret dulu, baru
// menukarnya kalau orientasinya landscape, jadi urutan angka di sini tidak
// lagi menentukan hasil akhir.
export const UKURAN_DIM_MM = {
  A4: { width: 210, height: 297 },
  A5: { width: 148, height: 210 },
  continuous_half: { width: 210, height: 140 },
  thermal_58: { width: 58, height: 200 },
  thermal_80: { width: 80, height: 250 },
};

// Orientasi BAWAAN per ukuran kertas — HARUS sama dengan ORIENTASI_BAWAAN di
// backend (routers/faktur.py). Dipakai hanya kalau template tidak menyebut
// orientasi sama sekali (dokumen lama / entitas mentah tanpa template
// lengkap), supaya cetakan template lama tidak berubah sedikit pun.
export const ORIENTASI_BAWAAN_UKURAN = {
  continuous_half: "landscape",
  thermal_58: "portrait",
  thermal_80: "portrait",
};

// Margin @page yang dipakai komponen ini. Dipakai juga untuk menghitung lebar
// area cetak efektif saat "paskan otomatis ke lebar kertas" — kalau angka ini
// diubah, perhitungan fit ikut benar dengan sendirinya (satu sumber).
export const MARGIN_CETAK_MM = 10;

// Batas skala cetak — HARUS sama dengan SKALA_CETAK_MIN/MAKS di backend
// (routers/faktur.py). Dipakai sebagai jaring pengaman terakhir sebelum nilai
// dituangkan ke CSS: dokumen korup / respons backend lama tidak boleh bisa
// menghasilkan `zoom: 0` yang membuat kertas keluar KOSONG.
export const SKALA_CETAK_MIN = 50;
export const SKALA_CETAK_MAKS = 150;
export const SKALA_CETAK_DEFAULT = 100;

// Kelas Tailwind max-w untuk tiap lebar mm yang mungkin muncul dari
// `resolveDim` (dasar ATAU hasil tukar lebar/tinggi saat landscape) — dipakai
// sebagai peta statis (bukan template literal dinamis) supaya semua kelasnya
// tetap terlihat oleh Tailwind JIT saat build.
const MM_WIDTH_CLASS = {
  58: "max-w-[58mm]",
  80: "max-w-[80mm]",
  140: "max-w-[140mm]",
  148: "max-w-[148mm]",
  200: "max-w-[200mm]",
  210: "max-w-[210mm]",
  250: "max-w-[250mm]",
  297: "max-w-[297mm]",
};

/** Resolusi dimensi cetak sesuai ukuran kertas & orientasi template.
 *
 *  Pasangan di UKURAN_DIM_MM DINORMALKAN ke potret dulu (sisi pendek jadi
 *  lebar), baru ditukar kalau orientasinya landscape. Cara lama — "tukar
 *  hanya kalau dasarnya belum landscape" — bekerja selama continuous_half
 *  DIPAKSA landscape oleh backend; sejak owner boleh memilih (brief 30 Agu
 *  2026 poin 4), memilih "portrait" untuk continuous_half tidak berefek apa
 *  pun dengan cara lama, karena dasarnya kebetulan sudah mendatar.
 *
 *  Orientasi kosong/tidak dikenal jatuh ke ORIENTASI_BAWAAN_UKURAN — nilainya
 *  sama persis dengan paksaan lama, jadi template lama tidak berubah. */
export function resolveDim(ukuran, orientasi) {
  const base = UKURAN_DIM_MM[ukuran] || UKURAN_DIM_MM.A4;
  const potret = base.width <= base.height
    ? base
    : { width: base.height, height: base.width };
  const diminta = String(orientasi || "").trim().toLowerCase();
  const dipakai = (diminta === "portrait" || diminta === "landscape")
    ? diminta
    : (ORIENTASI_BAWAAN_UKURAN[ukuran] || "portrait");
  return dipakai === "landscape"
    ? { width: potret.height, height: potret.width }
    : potret;
}

/** Skala cetak yang aman dipakai (persen, bilangan bulat). Nilai kosong/ngawur
 *  jatuh ke 100 = perilaku sebelum fitur "fit to paper" ada. */
export function normalisasiSkala(skala) {
  const n = Math.round(Number(skala));
  if (!Number.isFinite(n)) return SKALA_CETAK_DEFAULT;
  if (n < SKALA_CETAK_MIN || n > SKALA_CETAK_MAKS) return SKALA_CETAK_DEFAULT;
  return n;
}

/** mm -> px CSS (96dpi). Dipakai untuk membandingkan lebar konten yang diukur
 *  browser (px) dengan lebar kertas yang ditulis di @page (mm). */
export function mmKePx(mm) {
  return (Number(mm) || 0) * 96 / 25.4;
}

/** Skala AKHIR yang dipakai saat mencetak, dalam persen.
 *
 *  `fitOtomatis` = "paskan ke lebar kertas": lebar konten APA ADANYA diukur di
 *  browser lalu dibandingkan dengan lebar kertas dikurangi margin. Sengaja
 *  TIDAK PERNAH MEMBESARKAN (dibatasi 100): memperbesar dokumen yang sudah
 *  muat justru bisa mendorong isinya ke halaman kedua — dan keluhan yang mau
 *  ditutup adalah "tercetak tidak sesuai kertas", bukan "terlalu kecil".
 *  Pengukuran yang gagal (0/NaN — elemen belum ter-layout, dsb) mundur ke
 *  skala manual, bukan menghasilkan zoom aneh.
 *
 *  Dipisah jadi fungsi murni supaya aturannya bisa diuji tanpa DOM. */
export function hitungSkalaCetak({ skala, fitOtomatis, lebarKontenPx, lebarCetakPx }) {
  const manual = normalisasiSkala(skala);
  if (!fitOtomatis) return manual;
  const konten = Number(lebarKontenPx);
  const kertas = Number(lebarCetakPx);
  if (!(konten > 0) || !(kertas > 0)) return manual;
  const persen = Math.floor((kertas / konten) * 100);
  return Math.max(SKALA_CETAK_MIN, Math.min(SKALA_CETAK_DEFAULT, persen));
}

const JENIS_JUDUL_DEFAULT = {
  penjualan: "FAKTUR PENJUALAN",
  pembelian: "FAKTUR PEMBELIAN",
  pindah_gudang: "SURAT JALAN PINDAH GUDANG",
  retur_penjualan: "RETUR PENJUALAN",
  retur_pembelian: "RETUR PEMBELIAN",
  struk: "STRUK PENJUALAN",
  internal_memo: "INTERNAL MEMORANDUM",
};

/** Format rupiah TANPA spasi setelah "Rp" (mis. "Rp1.250.000") — konvensi
 *  dokumen cetak, sama persis dengan `_fmt_rupiah` di backend
 *  (routers/faktur.py) yang dipakai untuk pesan WhatsApp. Sengaja dibuat
 *  lokal di sini (bukan memakai `idr()` dari lib/api.js yang menyisipkan
 *  spasi "Rp 1.250.000") supaya faktur/struk konsisten dengan pesan WA yang
 *  dikirim untuk transaksi yang sama. */
export function rp(n) {
  return "Rp" + Math.round(Number(n) || 0).toLocaleString("id-ID");
}

/** Membongkar wadah cetak sementara. Aman dipanggil berkali-kali. */
export function bersihkanWadahCetak() {
  document.body.classList.remove("printing-doc");
  const lama = document.getElementById("print-root");
  if (lama) lama.remove();
}

/** Menyalin area cetak ke sebuah wadah yang menjadi ANAK LANGSUNG <body>.
 *
 *  KENAPA harus disalin keluar, bukan sekadar disembunyikan tetangganya:
 *  dokumen cetak dirender di dalam modal `position: fixed`, dan elemen fixed
 *  DIULANG oleh browser di SETIAP halaman kertas. Ditambah pendekatan lama
 *  yang memakai `visibility: hidden` — yang menyembunyikan latar tapi TETAP
 *  menyisakan ruangnya — halaman jadi terbentuk banyak, dan tiap halaman
 *  memuat salinan penuh fakturnya. Itulah sebab keluhan "cetak keluar 3-4
 *  rangkap": bukan template yang mencetak berkali-kali.
 *
 *  Diverifikasi dengan mencetak ke PDF lewat Chrome headless pada replika
 *  struktur yang sama: sebelum = 3 halaman berisi 3 salinan, sesudah = 1.
 *
 *  Salinan (bukan pemindahan) supaya React tidak pernah kehilangan node yang
 *  dikelolanya kalau terjadi render ulang selagi dialog cetak terbuka. */
/** Ukur lebar konten APA ADANYA (tanpa dipaksa muat) dari sebuah klon yang
 *  sudah menempel di DOM tapi belum tampil. Dipakai "paskan otomatis ke lebar
 *  kertas".
 *
 *  Kenapa harus diukur di klon, bukan di elemen aslinya: elemen asli dibatasi
 *  `max-w-[210mm]` supaya pratinjau layar rapi, jadi lebarnya SELALU terbaca
 *  segitu — pengukuran akan selalu bilang "sudah muat" dan fitnya tidak
 *  pernah bekerja. Di klon, batas itu dilepas sementara dan lebarnya
 *  dipaksa `max-content` sehingga yang terbaca adalah lebar yang BENAR-BENAR
 *  dibutuhkan isinya. Wadahnya digeser jauh ke kiri (bukan display:none)
 *  karena elemen yang tidak dirender tidak punya ukuran sama sekali.
 *  Dikembalikan 0 kalau gagal — pemanggil mundur ke skala manual. */
function ukurLebarKonten(root, klon) {
  const gayaRootLama = root.getAttribute("style") || "";
  const gayaKlonLama = klon.getAttribute("style") || "";
  let lebar = 0;
  try {
    root.style.cssText = "display:block;position:absolute;left:-10000px;top:0;width:auto;visibility:hidden";
    klon.style.maxWidth = "none";
    klon.style.width = "max-content";
    klon.style.zoom = "1";
    lebar = klon.getBoundingClientRect().width || 0;
  } catch (e) {
    lebar = 0;
  } finally {
    root.setAttribute("style", gayaRootLama);
    klon.setAttribute("style", gayaKlonLama);
  }
  return lebar;
}

/** Terapkan skala cetak pada KLON (bukan elemen asli di layar).
 *
 *  Sumber angkanya adalah atribut `data-*` yang ditempel komponen cetak pada
 *  `.print-area` — bukan argumen fungsi — supaya SEMUA pemanggil `cetak()`
 *  yang sudah ada (Sales.jsx, InventoryPO.jsx, Retur.jsx, OpnameBlind.jsx,
 *  MemoPrint, SuratTagihanPrint, FakturPublik) ikut menghormati pengaturan
 *  template tanpa satu pun dari mereka perlu diubah. Elemen tanpa atribut itu
 *  (mis. struk thermal) menghasilkan skala 100 = perilaku lama persis.
 *
 *  Dipakai `zoom`, BUKAN `transform: scale()`: transform melepas elemen dari
 *  alur tata letak sehingga dokumen yang lebih dari satu halaman terpotong di
 *  halaman kedua, sedangkan `zoom` membuat isinya di-layout ulang dan tetap
 *  terpecah halaman dengan benar. `zoom` selalu ditulis eksplisit di klon,
 *  jadi nilai zoom pratinjau yang ikut tersalin tidak pernah terkali dua. */
function terapkanSkalaPadaKlon(root, klon, asli) {
  // Pagar terakhir: apa pun yang salah di sini TIDAK boleh membuat tombol
  // Cetak mati — dokumen tetap harus keluar, paling banter tanpa penskalaan.
  try {
    if (typeof asli.getAttribute !== "function") return SKALA_CETAK_DEFAULT;
    const skala = Number(asli.getAttribute("data-skala-cetak"));
    const fitOtomatis = asli.getAttribute("data-fit-otomatis") === "1";
    const lebarCetakPx = mmKePx(Number(asli.getAttribute("data-lebar-cetak-mm")));
    const lebarKontenPx = fitOtomatis ? ukurLebarKonten(root, klon) : 0;
    const akhir = hitungSkalaCetak({ skala, fitOtomatis, lebarKontenPx, lebarCetakPx });
    klon.style.zoom = String(akhir / 100);
    return akhir;
  } catch (e) {
    klon.style.zoom = "1";
    return SKALA_CETAK_DEFAULT;
  }
}

export function siapkanWadahCetak(el) {
  if (!el) return false;
  bersihkanWadahCetak();
  const root = document.createElement("div");
  root.id = "print-root";
  const klon = el.cloneNode(true);
  root.appendChild(klon);
  document.body.appendChild(root);
  terapkanSkalaPadaKlon(root, klon, el);
  document.body.classList.add("printing-doc");
  return true;
}

/** Pembungkus window.print() — dipakai tombol cetak bawaan komponen ini, dan
 *  boleh diimpor halaman lain yang memasang tombol cetak sendiri.
 *  `el`: elemen `.print-area` yang mau dicetak. Kalau tidak diberikan, dicari
 *  sendiri di DOM (dipakai pemanggil lama yang belum meneruskan ref). */
export function cetak(el) {
  // Pemanggil lama memakai `onClick={cetak}`, sehingga argumen pertamanya
  // adalah objek event React — bukan elemen. Saring dulu supaya tidak
  // mencoba meng-clone event.
  const kandidat = el && typeof el.cloneNode === "function" ? el : null;
  const area = kandidat || document.querySelector(".print-area");
  const siap = siapkanWadahCetak(area);
  if (!siap) { window.print(); return; }   // tak ada area: jangan sampai tombol mati total
  const selesai = () => { bersihkanWadahCetak(); window.removeEventListener("afterprint", selesai); };
  window.addEventListener("afterprint", selesai);
  window.print();
  // Jaring pengaman: sebagian peramban tidak selalu memicu `afterprint`.
  setTimeout(selesai, 1500);
}

/** true kalau template ini seharusnya dirender StrukPrint (thermal), bukan
 *  FakturPrint (A4/A5/continuous_half). Satu tempat dipakai bersama oleh
 *  FakturSettings (pratinjau hidup) & FakturPublik supaya logikanya tidak
 *  bercabang di banyak tempat. */
export function isStruk(template) {
  const jenis = template?.jenis;
  const ukuran = template?.ukuran_kertas || "";
  return jenis === "struk" || ukuran.startsWith("thermal");
}

/** Terima entitas mentah (sale/po/transfer) ATAU seluruh payload endpoint
 *  data-cetak/faktur-publik yang membungkusnya — keduanya menghasilkan
 *  entitas yang sama untuk dirender. */
export function normalisasiEntitas(data) {
  if (!data) return {};
  return data.sale || data.po || data.transfer || data;
}

/** Satu bentuk baris item untuk sale/PO/transfer — field asalnya berbeda-beda
 *  (nama_barang vs nama, harga vs harga_per_unit vs nilai/qty, imei tunggal vs
 *  daftar unit ber-IMEI pada PO) — dirapikan di sini sekali saja.
 *
 *  Paket/bundling (poin 10, brief bos 23 Agu 2026): baris yang `sku`-nya
 *  adalah sku_bundle sebuah resep ditulis backend sebagai baris ITU SENDIRI
 *  (pendapatan) + baris SINTETIK per komponen ber-tanda `is_bundle_component`
 *  (routers/inventory/bundling.py::ekspansi_komponen_bundle_untuk_penjualan)
 *  — baris sintetik itu DISARING KELUAR di sini (tidak pernah tampil sebagai
 *  baris berdiri sendiri, harganya memang 0/bonus) dan sebaliknya
 *  dimunculkan sebagai SUB-BARIS di bawah baris paket induknya lewat
 *  `entity.items[].komponen` — SNAPSHOT APA ADANYA yang ditulis backend saat
 *  nota dibuat/disetujui, BUKAN dihitung ulang dari resep saat cetak (resep
 *  bisa berubah setelah nota ini ada — mencari ulang saat cetak berarti
 *  faktur LAMA mencetak komposisi yang SALAH, lihat CLAUDE.md pola snapshot
 *  `inventory_receipts.selisih_baris`). */
export function normalisasiItems(entity) {
  return (entity?.items || [])
    .filter((it) => !it.is_bundle_component)
    .map((it, idx) => {
      const qty = it.qty || 1;
      const hargaSatuan = it.addition ? Number(it.harga || 0) / qty : it.harga ?? it.harga_per_unit ?? (it.nilai && qty ? it.nilai / qty : 0);
      const total = it.nilai ?? hargaSatuan * qty;
      const imeiList = it.imei
        ? [it.imei]
        : (it.units || []).map((u) => u.identifier).filter(Boolean);
      // IMEI yang BENAR-BENAR diterima gudang (poin 13-B, brief bos 23 Agu
      // 2026) — beda sumber dari `imeiList` di atas, yang cuma IMEI DIPESAN.
      // Backend (faktur.py::data_faktur_pembelian) hanya mengisi
      // `units_diterima` untuk item PO ber-IMEI; array (walau kosong) berarti
      // "sudah dicek, ini hasilnya", jadi dibedakan dari `null` (field tidak
      // ada sama sekali — dokumen non-PO, atau respons backend lama) supaya
      // pemanggil tahu kapan boleh mundur ke `imeiList`.
      const imeiDiterima = Array.isArray(it.units_diterima)
        ? it.units_diterima.filter(Boolean)
        : null;
      const komponen = Array.isArray(it.komponen)
        ? it.komponen.map((k, ki) => ({
          key: `${it.sku || idx}-komp-${ki}`,
          nama: k.nama || k.sku || "-",
          sku: k.sku || "",
          qty: k.qty || 1,
          imei: k.imei || "",
        }))
        : [];
      return {
        key: it.sku ? `${it.sku}-${idx}` : idx,
        nama: it.nama_barang || it.nama || "-",
        box: it.box, addition: it.addition,
        sku: it.sku || "",
        imeiList,
        imeiDiterima,
        qty,
        harga: hargaSatuan,
        total,
        // Diskon PRODUK per baris (brief 22 Sep D5) — hanya penjualan yang
        // punya; dokumen lain & nota lama = 0 → baris tercetak persis seperti dulu.
        diskon: Number(it.diskon) || 0,
        komponen,
      };
    });
}

// `rincianPenjualanCetak` & `labelTempo` (brief 22 Sep B3/D5/D7) tinggal di
// `lib/notaPenjualan.js` — satu sumber untuk faktur A4, struk thermal, dan
// layar Penjualan, dan fungsi murni tanpa impor sehingga bisa diuji di Node
// (`backend/tests/test_layar_nota_brief22.py`).

/** Untuk faktur PEMBELIAN saja (poin 13-B, brief bos 23 Agu 2026): pilih SATU
 *  daftar IMEI untuk ditampilkan + labelnya — TIDAK PERNAH mencampur "yang
 *  diterima" dengan "yang dipesan" pada baris yang sama. Prioritas: unit yang
 *  BENAR-BENAR diterima gudang (`imeiDiterima`, bisa beda dari yang dipesan —
 *  lihat routers/retur.py), mundur ke IMEI yang dipesan (`imeiList`) kalau
 *  penerimaan untuk item ini belum terjadi sama sekali (PO baru dibuat, atau
 *  respons backend lama yang belum mengirim `units_diterima`). `null` = tidak
 *  ada IMEI sama sekali untuk item ini (barang non-serial) — pemanggil tidak
 *  merender apa pun, tidak menyisakan baris kosong yang menggantung. */
function imeiPembelian(it) {
  if (it.imeiDiterima && it.imeiDiterima.length > 0) {
    return { label: "IMEI Diterima", list: it.imeiDiterima };
  }
  if (it.imeiList.length > 0) {
    return { label: "IMEI Dipesan", list: it.imeiList };
  }
  return null;
}

export default function FakturPrint({
  data, template, publik = false, noFaktur, csatLink, garansi, tampilkanTombolCetak = true,
}) {
  const entity = normalisasiEntitas(data);
  const tpl = template || data?.template || {};
  const opsi = tpl.opsi || {};
  const kop = tpl.kop || {};
  const jenis = tpl.jenis || "penjualan";
  const ukuran = tpl.ukuran_kertas || "A4";
  const dim = resolveDim(ukuran, tpl.orientasi);
  // "Fit to paper" (owner 30 Agu 2026 poin 4). Template lama tidak punya
  // field ini -> 100% & fit mati -> cetakan identik dengan sebelumnya.
  const skalaCetak = normalisasiSkala(tpl.skala_cetak);
  const fitOtomatis = !!tpl.fit_otomatis;
  // Lebar area cetak efektif = lebar kertas dikurangi margin @page kiri+kanan.
  const lebarCetakMm = Math.max(1, dim.width - 2 * MARGIN_CETAK_MM);
  const items = normalisasiItems(entity);
  const noFakturFinal = noFaktur || data?.no_faktur || entity.code || "-";
  const csat = csatLink || data?.csat_link || null;
  const tampilSku = opsi.tampil_sku !== false;
  const tampilImei = opsi.tampil_imei !== false;

  // Logo kop: kop.logo_url (base64 data URL atau URL relatif) kalau ada dan
  // berhasil dimuat. Kosong ATAU gagal dimuat -> jatuh ke perilaku lama
  // (kotak berisi huruf pertama judul), supaya kop tidak pernah kosong
  // melompong di kertas.
  const [logoGagal, setLogoGagal] = useState(false);
  useEffect(() => { setLogoGagal(false); }, [kop.logo_url]);
  const tampilkanLogoGambar = kop.tampil_logo !== false && !!kop.logo_url && !logoGagal;
  const tampilkanLogoHuruf = kop.tampil_logo !== false && !tampilkanLogoGambar;

  // PPN (keputusan owner 18 Agu 2026): dokumen pajak WAJIB memisahkan DPP dan
  // PPN — kalau `entity.kena_ppn`, Total yang dicetak adalah DPP+PPN (yang
  // BENAR-BENAR ditagih ke pelanggan), bukan `total_harga` polos (yang di
  // `sales` SELALU DPP saja, lihat routers/sales.py). Field absen pada
  // dokumen lain (PO/transfer/retur) atau nota tanpa PPN -> perilaku IDENTIK
  // seperti sebelum fitur ini ada.
  const dppDokumen = entity.dpp ?? entity.total_harga ?? 0;
  const ppnDokumen = entity.ppn_nominal ?? 0;
  // Dokumen PENJUALAN dari server baru membawa `nilai_nota` (B3): Total yang
  // dicetak = nilai nota (sudah dikurangi diskon, + admin + PPN) — dulu faktur
  // mencetak total bruto dan pelanggan bertempo melihat angka yang berbeda dari
  // surat tagihannya. Dokumen lain tetap memakai rumus lama di bawah.
  const rincianJual = rincianPenjualanCetak(entity);
  const totalDokumen = rincianJual
    ? rincianJual.total
    : entity.kena_ppn
      ? dppDokumen + ppnDokumen
      : (entity.total_harga ?? entity.nilai_total ?? entity.harga_terima
        ?? items.reduce((a, it) => a + it.total, 0));

  // Pihak lawan berbeda sesuai jenis dokumen — sale punya pelanggan, PO punya
  // supplier, pindah gudang punya gudang tujuan/asal.
  let pihakLabel = "Pelanggan";
  let pihakNama = entity.nama_pelanggan || "-";
  let pihakSub = entity.no_hp || "";
  if (jenis === "pembelian" || jenis === "retur_pembelian") {
    pihakLabel = "Supplier";
    pihakNama = entity.supplier_nama || "-";
    pihakSub = entity.no_po_supplier ? `No. PO Supplier: ${entity.no_po_supplier}`
      : entity.po_code ? `PO Asal: ${entity.po_code}` : "";
  } else if (jenis === "pindah_gudang") {
    pihakLabel = "Tujuan";
    pihakNama = entity.ke_warehouse_nama || "-";
    pihakSub = entity.dari_warehouse_nama ? `Dari: ${entity.dari_warehouse_nama}` : "";
  }

  return (
    <div className="w-full">
      {/* @page khusus ukuran kertas template ini — dirender lewat <style> supaya
          tetap berlaku walau elemen di sekitarnya disembunyikan `.no-print`.
          Urutan `size: <lebar> <tinggi>` inilah yang menentukan portrait vs
          landscape di kertas; `resolveDim` sudah menukarnya sesuai orientasi. */}
      <style>{`@media print { @page { size: ${dim.width}mm ${dim.height}mm; margin: ${MARGIN_CETAK_MM}mm; } }`}</style>

      {tampilkanTombolCetak && (
        <div className="no-print flex items-center justify-end gap-3 mb-3">
          {/* Jujur soal apa yang akan keluar dari printer — orientasi & skala
              tidak kelihatan dari pratinjau layar (layar tidak punya "kertas"). */}
          <span className="text-[11px] text-slate-400" data-testid="faktur-info-kertas">
            {dim.width}×{dim.height}mm · {dim.width > dim.height ? "Landscape" : "Portrait"} ·{" "}
            {fitOtomatis ? "skala otomatis (paskan ke lebar kertas)" : `skala ${skalaCetak}%`}
          </span>
          <button type="button" onClick={cetak} data-testid="btn-cetak-faktur"
            className="inline-flex items-center gap-2 h-9 px-4 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50">
            <Printer className="w-4 h-4" /> Cetak
          </button>
        </div>
      )}

      {/* `data-skala-cetak` / `data-fit-otomatis` / `data-lebar-cetak-mm`
          dibaca `siapkanWadahCetak` saat mencetak (lihat
          `terapkanSkalaPadaKlon`) — sengaja lewat atribut DOM, bukan argumen
          fungsi, supaya SEMUA pemanggil `cetak()` yang sudah ada ikut
          menghormati pengaturan template tanpa satu pun perlu diubah.
          `zoom` pada pratinjau layar: owner melihat akibat skalanya sebelum
          membuang kertas. Saat "fit otomatis" aktif angkanya baru diketahui
          waktu mencetak (harus diukur dulu), jadi layar tetap 100% dan
          keterangannya ada di baris info di atas. */}
      <div
        data-testid="faktur-print-area"
        data-skala-cetak={skalaCetak}
        data-fit-otomatis={fitOtomatis ? "1" : "0"}
        data-lebar-cetak-mm={lebarCetakMm}
        style={!fitOtomatis && skalaCetak !== SKALA_CETAK_DEFAULT
          ? { zoom: skalaCetak / 100 } : undefined}
        className={`print-area mx-auto bg-white border border-slate-200 rounded-xl shadow-sm p-6 text-slate-900 overflow-x-auto ${MM_WIDTH_CLASS[dim.width] || ""}`}
      >
        {/* Kop */}
        <div className="flex items-start justify-between gap-4 border-b-2 border-slate-900 pb-4 mb-4">
          <div className="flex items-start gap-3 min-w-0">
            {tampilkanLogoGambar && (
              <img
                src={kop.logo_url}
                alt={kop.judul || "Logo"}
                data-testid="faktur-logo-gambar"
                onError={() => setLogoGagal(true)}
                className="w-12 h-12 rounded-lg object-contain shrink-0 bg-white border border-slate-100"
              />
            )}
            {tampilkanLogoHuruf && (
              <div className="w-12 h-12 rounded-lg bg-slate-900 text-white grid place-items-center font-display font-bold text-lg shrink-0">
                {(kop.judul || "").trim().charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <p className="font-display font-bold text-lg leading-tight">
                {kop.judul || JENIS_JUDUL_DEFAULT[jenis] || "FAKTUR"}
              </p>
              {kop.alamat && <p className="text-xs text-slate-500 mt-0.5">{kop.alamat}</p>}
              {kop.telepon && <p className="text-xs text-slate-500">Telp: {kop.telepon}</p>}
            </div>
          </div>
          <div className="text-right shrink-0">
            <p className="text-xs text-slate-500">No. Dokumen</p>
            <p className="font-mono font-semibold text-sm" data-testid="faktur-no-dokumen">{noFakturFinal}</p>
            <p className="text-xs text-slate-500 mt-1">
              {entity.tanggal || "-"}{entity.waktu ? ` · ${entity.waktu}` : ""}
            </p>
          </div>
        </div>

        {/* Pihak */}
        <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-slate-500">{pihakLabel}</p>
            <p className="font-semibold">{pihakNama}</p>
            {pihakSub && <p className="text-xs text-slate-500">{pihakSub}</p>}
          </div>
          <div className="text-right">
            {entity.nama_penjual && (
              <>
                <p className="text-[11px] uppercase tracking-wider text-slate-500">Penjual</p>
                <p className="font-semibold">{entity.nama_penjual}</p>
              </>
            )}
            {entity.branch && <p className="text-xs text-slate-500">{entity.branch}</p>}
            {entity.warehouse_nama && <p className="text-xs text-slate-500">{entity.warehouse_nama}</p>}
            {entity.cabang && <p className="text-xs text-slate-500">{entity.cabang}</p>}
          </div>
        </div>

        {/* Tabel item */}
        <table className="w-full text-sm border-collapse mb-4">
          <thead>
            <tr className="border-b border-slate-300 text-[11px] uppercase tracking-wider text-slate-500">
              <th className="text-left py-1.5 pr-2">Nama Barang</th>
              {tampilSku && <th className="text-left py-1.5 pr-2">SKU</th>}
              <th className="text-right py-1.5 pr-2">Qty</th>
              <th className="text-right py-1.5 pr-2">Harga</th>
              <th className="text-right py-1.5">Total</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={tampilSku ? 5 : 4} className="text-center text-slate-400 py-4 text-xs">Tidak ada barang</td></tr>
            ) : items.map((it) => {
              // Kolom IMEI berbaris ke bawah + bernomor HANYA untuk faktur
              // pembelian (poin 13-A) — bos tidak minta ini untuk penjualan/
              // struk/retur/pindah gudang, dan struk termal terlalu sempit
              // untuk daftar panjang. Jenis lain tetap satu baris seperti
              // sebelumnya (`it.imeiList` digabung koma).
              const sumberPembelian = jenis === "pembelian" ? imeiPembelian(it) : null;
              return (
                <Fragment key={it.key}>
                  <tr className="border-b border-slate-100" data-testid={`faktur-item-${it.key}`}>
                    <td className="py-2 pr-2 align-top">
                      {it.nama}
                      {it.addition && <small className="block">{it.addition.mode}{it.addition.target_imei ? ` · Aktivasi IMEI ${it.addition.target_imei}` : ""}{it.addition.temporary ? " · Box sementara, box yang dijanjikan tetap menyusul" : ""}</small>}
                      {it.box && <small className="block">Box: {it.box.mode}{it.box.mode === "Menyusul" ? ` · ${it.box.promised} · Tenggat ${it.box.due_date}` : ""}</small>}
                      {tampilImei && sumberPembelian && (
                        <div className="mt-1" data-testid={`faktur-imei-list-${it.key}`}>
                          <span className="block text-[9px] font-semibold uppercase tracking-wide text-slate-400">
                            {sumberPembelian.label}
                          </span>
                          <ol className="mt-0.5 list-decimal pl-3.5 text-[10px] font-mono text-slate-500 space-y-0.5">
                            {sumberPembelian.list.map((im, i) => (
                              <li key={`${it.key}-imei-${i}`}>{im}</li>
                            ))}
                          </ol>
                        </div>
                      )}
                      {tampilImei && jenis !== "pembelian" && it.imeiList.length > 0 && (
                        <span className="block text-[10px] font-mono text-slate-500">IMEI {it.imeiList.join(", ")}</span>
                      )}
                      {/* Diskon produk per baris (D5) — tercetak di barisnya. Kolom
                          Total tetap BRUTO supaya jumlah kolomnya = Subtotal di
                          bawah, lalu Diskon, lalu Total (nilai nota). */}
                      {it.diskon > 0 && (
                        <span className="block text-[10px] text-rose-700" data-testid={`faktur-item-diskon-${it.key}`}>
                          Diskon −{rp(it.diskon)}
                        </span>
                      )}
                    </td>
                    {tampilSku && <td className="py-2 pr-2 font-mono text-xs text-slate-500 align-top">{it.sku || "-"}</td>}
                    <td className="py-2 pr-2 text-right align-top">{it.qty}</td>
                    <td className="py-2 pr-2 text-right font-mono align-top">{rp(it.harga)}</td>
                    <td className="py-2 text-right font-mono align-top">{rp(it.total)}</td>
                  </tr>
                  {/* Sub-baris komponen paket (poin 10, brief bos 23 Agu 2026) —
                      snapshot APA ADANYA dari `it.komponen` (lihat
                      `normalisasiItems`), tidak menambah kolom Harga/Total
                      (nilainya sudah termasuk di baris paket di atas). */}
                  {it.komponen.map((k) => (
                    <tr key={k.key} className="border-b border-slate-50 bg-slate-50/40 text-slate-500"
                        data-testid={`faktur-item-${it.key}-komponen-${k.key}`}>
                      <td className="py-1 pr-2 pl-4 text-xs align-top">
                        ↳ {k.nama}
                        {tampilImei && k.imei && (
                          <span className="block text-[10px] font-mono text-slate-400">IMEI {k.imei}</span>
                        )}
                      </td>
                      {tampilSku && <td className="py-1 pr-2 font-mono text-[10px] align-top">{k.sku || "-"}</td>}
                      <td className="py-1 pr-2 text-right text-xs align-top">{k.qty}</td>
                      <td className="py-1 pr-2 text-right align-top">—</td>
                      <td className="py-1 text-right align-top">—</td>
                    </tr>
                  ))}
                </Fragment>
              );
            })}
          </tbody>
        </table>

        {/* Trade-in (dokumen lama/bukan penjualan server baru — untuk penjualan
            baru trade-in tercetak sebagai PEMBAYARAN di bawah Total, D5) */}
        {!rincianJual && entity.trade_in && (entity.trade_in_items || []).length > 0 && (
          <div className="mb-4 border border-violet-100 bg-violet-50/50 rounded-lg p-3">
            <p className="text-xs font-semibold text-violet-800 mb-1.5">Barang Trade-in</p>
            {entity.trade_in_items.map((t, i) => (
              <div key={i} className="flex justify-between text-xs text-violet-900 py-0.5">
                <span>{t.nama}{t.imei ? ` — IMEI ${t.imei}` : ""}</span>
                <span className="font-mono">{rp(t.harga)}</span>
              </div>
            ))}
            {entity.trade_in_note && <p className="text-[11px] text-violet-700 mt-1.5">{entity.trade_in_note}</p>}
          </div>
        )}

        {/* Split pembayaran (dokumen lama — penjualan baru mencetaknya di bawah Total) */}
        {!rincianJual && entity.payments && entity.payments.length > 0 && (
          <div className="mb-4">
            <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Pembayaran</p>
            {entity.payments.map((p, i) => (
              <div key={i} className="flex justify-between text-sm py-0.5">
                <span className="text-slate-600">{p.method}</span>
                <span className="font-mono">{rp(p.amount)}</span>
              </div>
            ))}
          </div>
        )}

        {/* Catatan transaksi (mis. diisi kasir saat input nota) */}
        {opsi.tampil_catatan !== false && entity.catatan && (
          <div className="mb-4 text-xs text-slate-600 bg-slate-50 rounded-lg p-2.5">
            <span className="font-semibold">Catatan: </span>{entity.catatan}
          </div>
        )}

        {/* Catatan penjual — tetap milik TEMPLATE, berdampingan dengan catatan
            transaksi di atas (bukan menggantikannya). Kosong = tidak tampil
            sama sekali, tidak menyisakan ruang. */}
        {tpl.catatan_penjual && tpl.catatan_penjual.trim() && (
          <div className="mb-4 text-xs text-slate-600 bg-slate-50 rounded-lg p-2.5">
            <span className="font-semibold">Catatan Penjual: </span>{tpl.catatan_penjual}
          </div>
        )}

        {/* Total — DPP & PPN dicetak TERPISAH kalau dokumen ini kena PPN
            (syarat dokumen pajak, keputusan owner 18 Agu 2026). */}
        <div className="border-t-2 border-slate-900 pt-3 mb-4">
          {/* Penjualan: subtotal → diskon → biaya admin (nota lama) sebelum
              DPP/PPN, supaya Total = nilai nota terbaca asal-usulnya. */}
          {rincianJual && (rincianJual.diskon > 0 || rincianJual.biayaAdmin > 0) && (
            <div className="flex justify-end mb-1">
              <div className="text-right text-xs text-slate-600 space-y-0.5 min-w-[200px]" data-testid="faktur-rincian-diskon">
                <div className="flex justify-between gap-4">
                  <span>Subtotal</span>
                  <span className="font-mono">{rp(rincianJual.subtotal)}</span>
                </div>
                {rincianJual.diskon > 0 && (
                  <div className="flex justify-between gap-4 text-rose-700">
                    <span>Diskon</span>
                    <span className="font-mono" data-testid="faktur-diskon">−{rp(rincianJual.diskon)}</span>
                  </div>
                )}
                {rincianJual.biayaAdmin > 0 && (
                  <div className="flex justify-between gap-4">
                    <span>Biaya admin</span>
                    <span className="font-mono">{rp(rincianJual.biayaAdmin)}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          {entity.kena_ppn && (
            <div className="flex justify-end mb-2">
              <div className="text-right text-xs text-slate-600 space-y-0.5 min-w-[200px]" data-testid="faktur-ppn-breakdown">
                <div className="flex justify-between gap-4">
                  <span>DPP</span>
                  <span className="font-mono">{rp(dppDokumen)}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span>PPN ({entity.ppn_persen ?? 11}%)</span>
                  <span className="font-mono" data-testid="faktur-ppn-nominal">{rp(ppnDokumen)}</span>
                </div>
              </div>
            </div>
          )}
          <div className="flex justify-end">
            <div className="text-right">
              <p className="text-xs text-slate-500">Total</p>
              <p className="font-display font-bold text-xl" data-testid="faktur-total">{rp(totalDokumen)}</p>
            </div>
          </div>
          {/* Pembayaran penjualan (D4/D5/D7): baris metode + keterangan (No.
              Kontrak), trade-in SEBAGAI pembayaran, DP, lalu sisa tagihan nota
              bertempo (dari dokumen piutang). */}
          {rincianJual && (rincianJual.pembayaran.length > 0 || rincianJual.metodeTunggal || rincianJual.tempo) && (
            <div className="flex justify-end mt-2">
              <div className="text-xs text-slate-700 space-y-0.5 min-w-[240px]" data-testid="faktur-pembayaran">
                <p className="text-[11px] uppercase tracking-wider text-slate-500 text-right">Pembayaran</p>
                {rincianJual.pembayaran.map((p, i) => (
                  <div key={i} className="flex justify-between gap-4" data-testid={`faktur-bayar-${p.jenis}-${i}`}>
                    <span>
                      {p.label}
                      {p.keterangan && <span className="block text-[10px] text-slate-500">{p.keterangan}</span>}
                    </span>
                    <span className="font-mono">{rp(p.nilai)}</span>
                  </div>
                ))}
                {rincianJual.metodeTunggal && (
                  <div className="flex justify-between gap-4"><span>Metode</span><span>{rincianJual.metodeTunggal}</span></div>
                )}
                {rincianJual.tempo && (
                  <div className="flex justify-between gap-4 font-semibold border-t border-slate-200 pt-0.5" data-testid="faktur-sisa">
                    <span>
                      Sisa tagihan (Tempo {labelTempo(rincianJual.tempo)}
                      {rincianJual.jatuhTempo ? `, jatuh tempo ${rincianJual.jatuhTempo}` : ""})
                    </span>
                    <span className="font-mono">{rincianJual.sisa === null ? "-" : rp(rincianJual.sisa)}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Tautan CSAT — QR dibuat backend (csat.qr_data_url), TIDAK memuat dari
            layanan pihak ketiga di internet supaya tetap tampil di kasir
            tanpa koneksi. Kalau backend belum sempat membuatkan QR (field
            tidak ada), jangan sembunyikan diam-diam — tetap tampilkan
            tautannya sebagai teks supaya pelanggan bisa mengetiknya manual. */}
        {opsi.sertakan_link_csat && csat?.survey_url && (
          <div className="mb-4 flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
            {csat.qr_data_url ? (
              <img
                src={csat.qr_data_url}
                alt="QR penilaian pelayanan" data-testid="faktur-csat-qr"
                className="w-16 h-16 shrink-0 bg-white rounded"
              />
            ) : null}
            <div className="text-xs text-emerald-800 min-w-0">
              <p className="font-semibold mb-0.5">Nilai pelayanan kami</p>
              {!csat.qr_data_url && (
                <p className="text-[10px] text-emerald-700 mb-0.5">
                  Kode QR belum tersedia — ketik tautan berikut di browser:
                </p>
              )}
              <p className="break-all">{csat.survey_url}</p>
            </div>
          </div>
        )}

        {/* Blok TTD 3 kolom */}
        {opsi.tampil_ttd && (
          <div className="grid grid-cols-3 gap-4 mt-8 text-center text-xs">
            {["Pembuat", "Disetujui", "Diterima"].map((l) => (
              <div key={l}>
                <div className="h-16 border-b border-slate-300 mb-1" />
                <p className="text-slate-600">{l}</p>
              </div>
            ))}
          </div>
        )}

        {/* Syarat & Ketentuan — blok multi-baris TERPISAH dari footer_teks
            (footer tetap satu paragraf kecil rata tengah seperti sebelumnya). */}
        <WarrantyTerms warranty={garansi || data?.garansi} />
        {tpl.syarat_ketentuan && tpl.syarat_ketentuan.trim() && (
          <div className="mt-6 pt-3 border-t border-slate-100 text-[10px] text-slate-500">
            <p className="font-semibold text-slate-600 mb-1">Syarat &amp; Ketentuan</p>
            <p className="whitespace-pre-line">{tpl.syarat_ketentuan}</p>
          </div>
        )}

        {/* Footer */}
        {tpl.footer_teks && (
          <p className="text-center text-xs text-slate-500 mt-6 pt-3 border-t border-slate-100">{tpl.footer_teks}</p>
        )}
        {publik && (
          <p className="text-center text-[10px] text-slate-300 mt-2">
            Dokumen ini dibuat otomatis oleh sistem.
          </p>
        )}
      </div>
    </div>
  );
}
